import { ArrowLeftIcon, ArrowRightIcon } from "@innoscripta/icons";
import classNames from "classnames";
import { add } from "date-fns/add";
import { format } from "date-fns/format";
import { getMonth } from "date-fns/getMonth";
import { getYear } from "date-fns/getYear";
import { isSameMonth } from "date-fns/isSameMonth";
import { parse } from "date-fns/parse";
import { sub } from "date-fns/sub";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { TimeIntervals } from "./calendar.types";

import { useGetAvailableTimeSlots } from "../../hooks/api/use-get-available-time-slots";
import { useAppointmentStore } from "../../store/appointment/appointment-store";
import { useGeneralStore } from "../../store/general/general-store";
import { DATE_FORMAT, FIRST_DAY_OF_WEEK, MONTHS } from "./calendar.constants";
import { CalendarViews } from "./calendar.enums";
import {
  getDateFnsLocale,
  getWeekDays,
  getWeeksOfMonth,
} from "./calendar.helpers";
import styles from "./styles/calendar.module.scss";

type Props = {
  setCurrentView: React.Dispatch<React.SetStateAction<CalendarViews>>;
  dateToDisplay: Date;
  setDateToDisplay: React.Dispatch<React.SetStateAction<Date>>;
  onSelectDate?: () => void;
};

export function Weeks({
  setCurrentView,
  dateToDisplay,
  setDateToDisplay,
  onSelectDate,
}: Props) {
  const {
    resetTimeRange,
    setGlobalDateToDisplay,
    setDate,
    resetDate,
    setTimeRange,
    setTimeIntervals,
    setIsFetchingIntervals,
  } = useAppointmentStore();
  const { date } = useAppointmentStore(state => state.appointmentData);
  // removed unused isCalendarLoaded from earlier approach
  const { data, isFetching, isLoading } = useGetAvailableTimeSlots(dateToDisplay);
  const navigate = useNavigate();
  const { language } = useGeneralStore();
  const { t } = useTranslation();

  const selectDate = useCallback(
    (day: Date) => {
      const dayFormatted = format(day, DATE_FORMAT);

      setGlobalDateToDisplay(day);
      resetTimeRange();
      // Ensure we set the *full* available intervals from the latest data
      setTimeIntervals((data?.data as unknown as TimeIntervals) || {});

      onSelectDate?.();
      setDate(dayFormatted); // This updates the global date state
    },
    [
      date,
      setGlobalDateToDisplay,
      resetTimeRange,
      setTimeIntervals,
      data?.data,
      onSelectDate,
      setDate,
    ],
  );

  // Memoize weeks calculation for better performance
  const weeks = useMemo(() => {
    return getWeeksOfMonth(FIRST_DAY_OF_WEEK, dateToDisplay);
  }, [dateToDisplay]);

  useEffect(() => {
    // Only proceed if data is loaded and available
    if (data?.data && !isLoading) {
      const availableDates = Object.keys(data.data).sort();
      // Always populate intervals so time slots can render
      setTimeIntervals((data?.data as unknown as TimeIntervals));

      // Normalize today to date string for simple comparisons
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = format(today, DATE_FORMAT);

      const monthKey = format(dateToDisplay, "yyyy-MM");
      const firstDayOfDisplayedMonthStr = `${monthKey}-01`;
      const minSelectableStr
        = todayStr > firstDayOfDisplayedMonthStr ? todayStr : firstDayOfDisplayedMonthStr;

      const firstAvailableInDisplayedMonth = availableDates.find(
        d => d.startsWith(monthKey),
      );
      const earliestAvailableInDisplayedMonth = availableDates.find(
        d => d.startsWith(monthKey) && d >= minSelectableStr,
      );

      const isSelectedInDisplayedMonth = !!date && date.startsWith(monthKey);
      const isSelectedAvailable = !!date && availableDates.includes(date);

      // If the selected date is valid for the currently displayed month, keep it.
      if (isSelectedInDisplayedMonth && isSelectedAvailable) {
        return;
      }

      // When changing months, always select the earliest available day in THAT month
      // (but never before today).
      const nextAvailableStr
        = earliestAvailableInDisplayedMonth || firstAvailableInDisplayedMonth;

      if (nextAvailableStr) {
        const nextAvailable = parse(
          nextAvailableStr,
          DATE_FORMAT,
          new Date(),
        );
        nextAvailable.setHours(0, 0, 0, 0);
        selectDate(nextAvailable);
      }
    }
    // Depend on data, loading state, displayed month, selected date, and selection functions
  }, [data, isLoading, dateToDisplay, date, selectDate, setTimeIntervals]);

  useEffect(() => {
    setGlobalDateToDisplay(dateToDisplay);
  }, [dateToDisplay, setGlobalDateToDisplay]);

  useEffect(() => {
    // This effect handles resetting time when the month changes,
    // but should NOT reset the date itself, as the other effect handles selecting
    // the appropriate date for the new month.
    const displayedMonthKey = format(dateToDisplay, "yyyy-MM");
    const selectedMonthKey = date ? date.slice(0, 7) : null;

    if (selectedMonthKey && selectedMonthKey === displayedMonthKey) {
      return;
    }

    // Only clear time range on month change; do not clear selected date here.
    setTimeRange([]);
  }, [dateToDisplay, date, setTimeRange]);

  const displayPreviousMonth = () => {
    resetDate();
    resetTimeRange();
    const shifted = sub(dateToDisplay, {
      months: 1,
    });
    const newDate = new Date(getYear(shifted), getMonth(shifted), 1);
    setDateToDisplay(newDate);
    setGlobalDateToDisplay(newDate);
  };

  const displayNextMonth = () => {
    resetDate();
    resetTimeRange();
    const shifted = add(dateToDisplay, {
      months: 1,
    });
    const newDate = new Date(getYear(shifted), getMonth(shifted), 1);
    setDateToDisplay(newDate);
    setGlobalDateToDisplay(newDate);
  };

  useEffect(() => {
    setIsFetchingIntervals(isFetching);
  }, [isFetching, setIsFetchingIntervals]);

  useEffect(() => {
    if (!isLoading && (data?.code === 404 || data?.extra?.event_type?.is_active === 0)) {
      navigate("/404");
    }
  }, [isLoading, data, navigate]);

  return (
    <>
      <div className={styles.yearSelector}>
        <div
          className={styles.monthButton}
          onClick={!isLoading ? displayPreviousMonth : undefined}
          onKeyDown={
            !isLoading
              ? (key) => {
                  if (key.key === "Enter")
                    displayPreviousMonth();
                }
              : undefined
          }
          role="button"
          tabIndex={!isLoading ? 0 : -1}
          style={{
            opacity: isLoading ? 0.5 : 1,
            cursor: isLoading ? "not-allowed" : "pointer",
          }}
        >
          <ArrowLeftIcon />
        </div>
        <div className={styles.buttons}>
          <button
            type="button"
            onClick={() =>
              !isLoading && setCurrentView(CalendarViews.MonthSelector)}
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.5 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            {t(`months.${MONTHS[getMonth(dateToDisplay)]}`)}
            {" "}
            {getYear(dateToDisplay)}
          </button>
        </div>
        <div
          className={styles.monthButton}
          onClick={!isLoading ? displayNextMonth : undefined}
          onKeyDown={
            !isLoading
              ? (key) => {
                  if (key.key === "Enter")
                    displayNextMonth();
                }
              : undefined
          }
          role="button"
          tabIndex={!isLoading ? 0 : -1}
          style={{
            opacity: isLoading ? 0.5 : 1,
            cursor: isLoading ? "not-allowed" : "pointer",
          }}
        >
          <ArrowRightIcon />
        </div>
      </div>
      <div className={styles.weekLabels}>
        {getWeekDays(FIRST_DAY_OF_WEEK, getDateFnsLocale(language)).map(
          week => (
            <div className={styles.weekField} key={week.id}>
              {week.day.toUpperCase()}
            </div>
          ),
        )}
      </div>
      <div className={styles.weeks}>
        {weeks.map((week) => {
          const isSameWeek
            = date
              && week.week.map(day => format(day.day, DATE_FORMAT)).includes(date);

          const rowCn = classNames(styles.weekRow, {
            [styles.active]: isSameWeek && !isFetching,
          });
          return (
            <div className={rowCn} key={week.id}>
              {week.week.map((day) => {
                // Skip rendering days from other months
                if (!isSameMonth(day.day, dateToDisplay)) {
                  return <div className={styles.weekField} key={day.id} />;
                }

                const today = new Date();

                // resetting hours to 0:00.00 to compare the dates equally, not based on time
                today.setHours(0, 0, 0, 0);
                day.day.setHours(0, 0, 0, 0);

                const isToday
                  = format(day.day, DATE_FORMAT) === format(today, DATE_FORMAT);

                const isAvailable
                  = !isLoading && data?.data
                    ? Object.keys(data?.data)?.includes(
                      format(day.day, "yyyy-MM-dd"),
                    )
                    : false;
                const isBeforeToday = day.day < today;
                const isDisabled = isLoading || !isAvailable || isBeforeToday;

                const isRegularDay
                  = isSameMonth(day.day, dateToDisplay) && !isDisabled;

                const dayCn = classNames(
                  styles.day,
                  { [styles.regular]: isRegularDay },
                  {
                    [styles.anotherMonth]:
                      !isSameMonth(day.day, dateToDisplay) && !isDisabled,
                  },
                  { [styles.holiday]: false }, // TODO: implement holidays
                  { [styles.today]: isToday },
                  { [styles.loading]: isLoading },
                );

                const fieldCn = classNames(styles.weekField, {
                  [styles.selected]:
                    date && format(day.day, DATE_FORMAT) === date,
                });

                return (
                  <div className={fieldCn} key={day.id}>
                    <button
                      type="button"
                      className={dayCn}
                      onClick={() => {
                        if (!isLoading && data) {
                          // @ts-expect-error
                          selectDate(day.day, data.data);
                        }
                      }}
                      disabled={isDisabled}
                      style={{ opacity: isLoading ? 0.5 : 1 }}
                    >
                      {format(day.day, "d")}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
}
